import * as amplitude from '@amplitude/analytics-browser'
import { Box } from '@material-ui/core'
import { hardcodedReminderOffsets } from '@shared/core/custom-offsets'
import { maxMessageLength } from '@shared/core/events'
import { UserPreferencesResponse } from '@shared/core/preferences'
import { GetProfileResponseDto } from '@shared/core/users'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { BaseSyntheticEvent, ChangeEvent, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useFlag } from 'react-tggl-client'
import { createYupResolver } from 'src/helpers/custom-yup-resolver'
import generateRandomId from 'src/helpers/generate-random-id'
import { AppointmentConfirmationSteps } from 'src/modules/confirmation/components'
import { appointmentConfirmationSteps } from 'src/modules/confirmation/constants/confirmation.constants'
import {
  Button,
  FormattedText,
  FormControl,
  SecondaryHeader,
  SelectField,
  TextField,
  ToggleSwitch,
  Typography,
} from 'src/modules/core/components'
import { MessageInput } from 'src/modules/core/components/message-input/message-input'
import { getDefaultCountryCode, getPhoneAndCountryCode } from 'src/modules/core/utils/phone-input.utils'
import { buildMessageFieldPlaceholder } from 'src/modules/reminder/utils/dynamic-data-token-hints.utils'
import { generateEstimatedEventMessage } from 'src/modules/event/utils/message-builder'
import { configurationFieldNames, configurationSchema } from 'src/modules/user/constants/configuration-form'
import type { PhoneNumber, ProfileFormData } from 'src/modules/user/types/profile.types'
import { getPopulatedMessageTemplateDefaults } from 'src/modules/user/utils/onboarding-template-defaults'
import { resolveProfileMessageLanguage } from 'src/modules/user/utils/resolve-profile-message-language'
import { ApiService } from 'src/services/api.service'
import { SendTestOrFinishScreen } from '../send-test-or-finish'
import { ConfigurationFormPhoneNumbers } from './configuration-form-phone-numbers'
import classes from './configuration-form.module.scss'

const MAX_PHONE_NUMBERS = 10

export interface ConfigurationFormProps {
  profile: GetProfileResponseDto
  preferences: UserPreferencesResponse
}

type ConfigurationFormInternalProps = {
  profile: GetProfileResponseDto
  preferences: UserPreferencesResponse
  newWebAppOnboarding: boolean
  blankFields: boolean
}

export function ConfigurationForm({ profile, preferences }: ConfigurationFormProps) {
  const { value, loading } = useFlag('calendarEventXNewOnboarding', 'test_populated_calendar')
  if (loading) return null

  const useNewOnboarding = !!value?.includes('test')
  const blankFields = !value?.includes('populated')

  return (
    <ConfigurationFormInternal
      profile={profile}
      preferences={preferences}
      newWebAppOnboarding={useNewOnboarding}
      blankFields={blankFields}
    />
  )
}

function ConfigurationFormInternal({
  profile,
  preferences,
  newWebAppOnboarding,
  blankFields,
}: ConfigurationFormInternalProps) {
  const { formatMessage } = useIntl()

  const isPersonalMode = profile.usage_mode === 'personal'

  // The two modes describe the same step in different words; a personal-mode
  // account must not be greeted with the customer-mode copy.
  const headingIds = isPersonalMode
    ? { title: 'configuration.step1.personalMode.title', description: 'configuration.step1.personalMode.description' }
    : { title: 'configuration.step1.customerMode.title', description: 'configuration.step1.customerMode.description' }

  const [isLoading, setIsLoading] = useState(false)
  const [sendTestReminder, setSendTestReminder] = useState(false)
  const [showSendTestOrFinish, setShowSendTestOrFinish] = useState(false)
  const [noPhoneNumbersError, setNoPhoneNumbersError] = useState<string>(null)

  const defaultCountryCode = getDefaultCountryCode(profile)

  const templateDefaults = blankFields
    ? { from: '', location: '', reminderMessage: '', timing: [] as string[] }
    : getPopulatedMessageTemplateDefaults(profile)

  const defaultTemplateLanguage = profile.default_message_template?.language
  const messageLanguage = resolveProfileMessageLanguage(profile, defaultTemplateLanguage).language
  const reminderPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('reminder', messageLanguage),
    [messageLanguage],
  )

  const {
    register,
    handleSubmit,
    watch,
    control,
    setError,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<ProfileFormData>({
    resolver: createYupResolver(configurationSchema, configurationFieldNames),
    defaultValues: {
      from: templateDefaults.from,
      meetingLocation: templateDefaults.location,
      defaultReminder: templateDefaults.reminderMessage,
      timing: templateDefaults.timing,
      // Seeded from the profile: this form is also reached by long-standing
      // accounts that never edited their template, and re-defaulting to true
      // would silently switch client confirmations (and, likewise, reminders for
      // every appointment) back on for anyone who deliberately turned them off.
      appointmentConfirmation: profile.appointment_confirmation ?? true,
      phoneNumbers: isPersonalMode ? buildPhoneNumberDefaults(profile, defaultCountryCode) : [],
      personalRemindersForAllAppointments: profile.personal_reminders_for_all_appointments ?? true,
    },
  })

  const {
    fields: phoneNumbers,
    append: addPhoneNumber,
    remove: removePhoneNumber,
  } = useFieldArray({ control, name: 'phoneNumbers' })

  const { defaultReminder, meetingLocation, from, appointmentConfirmation, personalRemindersForAllAppointments } =
    watch()

  // Personal-mode accounts never get the client confirm/cancel instructions, so
  // the preview, the segment counter and the length gate must be computed
  // without them.
  const confirmationsEnabled = isPersonalMode ? false : appointmentConfirmation

  const {
    message: messagePreview,
    messageLength,
    segmentsCount,
  } = useMemo(
    () =>
      generateEstimatedEventMessage(
        'reminder',
        {
          ...profile,
          appointment_confirmation: confirmationsEnabled,
          default_message_template: {
            ...profile.default_message_template,
            sender_name: from,
            location: meetingLocation,
            reminder_message: defaultReminder,
          },
        },
        preferences,
        defaultTemplateLanguage,
      ),
    [profile, preferences, defaultReminder, from, meetingLocation, confirmationsEnabled, defaultTemplateLanguage],
  )

  function handleSendTestReminderClick() {
    amplitude.track('Onboarding_SendTestReminder_button')
    setSendTestReminder(true)
  }

  function handleSkipAndFinishClick() {
    amplitude.track('Onboarding_SkipandFinish_button')
    setSendTestReminder(false)
  }

  function handleAddPhoneNumber() {
    if (phoneNumbers.length < MAX_PHONE_NUMBERS) {
      addPhoneNumber({ id: generateRandomId(), country: defaultCountryCode, phone: '' })
    }
  }

  // `users.phone_numbers` is the only receiver source a personal-mode account
  // has, so an empty or invalid list must block the submit instead of leaving
  // the account unable to send. Returns null when the form must not be sent.
  function buildPersonalFields(data: ProfileFormData) {
    const { numbers, invalidIndex } = collectValidPhoneNumbers(data.phoneNumbers ?? [])
    if (invalidIndex !== null) {
      setError(`phoneNumbers.${invalidIndex}.phone`, { type: 'custom', message: 'Invalid phone number' })
      return null
    }

    setNoPhoneNumbersError(null)
    if (numbers.length === 0) {
      setNoPhoneNumbersError('Provide at least one valid phone number')
      return null
    }

    return {
      phone_numbers: numbers,
      personal_reminders_for_all_appointments: data.personalRemindersForAllAppointments,
    }
  }

  const onSubmit = async (data: ProfileFormData, event: BaseSyntheticEvent) => {
    event.preventDefault()

    const personalFields = isPersonalMode ? buildPersonalFields(data) : {}
    if (!personalFields) return

    if (messageLength > maxMessageLength) {
      setError('defaultReminder', {
        message: `Reminder message is too long`,
      })
      return
    }

    setIsLoading(true)
    await ApiService.Profile.updateProfile({
      // Personal-mode accounts do not see the confirmation control (the block is
      // rendered only when `!isPersonalMode`), and the personal form this one
      // replaced never submitted this field. Writing it here would switch client
      // confirmations on for every pre-existing personal account that is still
      // routed back through onboarding, which is a settings change they never
      // asked for.
      ...(isPersonalMode ? {} : { appointment_confirmation: data.appointmentConfirmation }),
      ...personalFields,
      default_message_template_location: data.meetingLocation,
      default_message_template_sender_name: data.from,
      default_message_template_reminder_message: data.defaultReminder,
      default_message_template_reminder_offsets: data.timing.map((time) => +time),
    }).finally(() => setIsLoading(false))

    setShowSendTestOrFinish(true)
  }

  return (
    <>
      <Box className="translate">
        <SecondaryHeader
          className={classes.text}
          title={<FormattedText id={headingIds.title} />}
          description={<FormattedText id={headingIds.description} />}
        />
        <form className={classes.root} onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.wrapper}>
            <TextField
              wrapperClassName={classes.textFieldWrapper}
              label={formatMessage({ id: 'configuration.step1.label.from' })}
              placeholder={formatMessage({
                id: 'configuration.step1.placeholder.from',
              })}
              {...register('from')}
              error={errors.from?.message}
            />

            {isPersonalMode && (
              <ConfigurationFormPhoneNumbers
                control={control}
                fields={phoneNumbers}
                errors={errors}
                defaultCountryCode={defaultCountryCode}
                noPhoneNumbersError={noPhoneNumbersError}
                canAddMore={phoneNumbers.length < MAX_PHONE_NUMBERS}
                onAdd={handleAddPhoneNumber}
                onRemove={removePhoneNumber}
              />
            )}

            <TextField
              wrapperClassName={classes.textFieldWrapper}
              label={formatMessage({
                id: 'configuration.step1.label.location',
              })}
              Element="textarea"
              rows={3}
              placeholder={formatMessage({
                id: 'configuration.step1.placeholder.location',
              })}
              {...register('meetingLocation')}
              error={errors.meetingLocation?.message}
            />

            <MessageInput
              wrapperClassName={classes.textFieldWrapper}
              labelClassName={classes.label}
              label={formatMessage({
                id: 'configuration.step1.label.reminder',
              })}
              placeholder={reminderPlaceholder}
              rows={3}
              {...register('defaultReminder')}
              error={errors.defaultReminder?.message}
              showDynamicDataHelper
              messageLanguage={messageLanguage}
              confirmationsEnabled={confirmationsEnabled}
              messagePreview={messagePreview}
              messageTemplate={defaultReminder}
              messageLength={messageLength}
              segmentsCount={segmentsCount}
            />

            <FormControl
              label={formatMessage({ id: 'configuration.step1.timing.title' })}
              error={errors.timing?.message}
            >
              <Controller
                control={control}
                defaultValue={[]}
                name="timing"
                render={({ field: { onChange, value, ref } }) => (
                  <SelectField
                    ref={ref}
                    value={hardcodedReminderOffsets.filter((c) => value.includes(c.value))}
                    onChange={(val) => onChange(val.map((c) => c.value))}
                    options={hardcodedReminderOffsets}
                    isMulti
                  />
                )}
              />
            </FormControl>

            {!isPersonalMode && (
              <div className={classes.confirmationWrapper}>
                <Typography variant="body">
                  <FormattedText id="configuration.step1.confirmation.title" />
                </Typography>
                <AppointmentConfirmationSteps steps={appointmentConfirmationSteps} />
                <Box display="flex" alignItems="center" marginTop={5}>
                  <Box marginRight={2}>
                    <ToggleSwitch
                      checked={appointmentConfirmation}
                      labelClassName={classes.switchLabel}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setValue('appointmentConfirmation', e.target.checked)
                      }
                    />
                  </Box>
                  <Typography variant="body">
                    <FormattedText id="configuration.step1.confirmation.label" />
                  </Typography>
                </Box>
              </div>
            )}

            {isPersonalMode && (
              <Box display="flex" alignItems="center" marginTop={5}>
                <Box marginRight={2}>
                  <ToggleSwitch
                    checked={personalRemindersForAllAppointments}
                    labelClassName={classes.switchLabel}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setValue('personalRemindersForAllAppointments', e.target.checked)
                    }
                  />
                </Box>
                <Typography variant="body">
                  <FormattedText id="configuration.step1.personalMode.autoReminders.title" />
                </Typography>
              </Box>
            )}
          </div>

          {isSubmitted && (Object.keys(errors).length > 0 || noPhoneNumbersError) && (
            <Box marginTop={5} className={classes.error}>
              Review the errors above to proceed
            </Box>
          )}

          {newWebAppOnboarding ? (
            <Box display="flex" gridGap={20} className="notranslate">
              <Button
                type="submit"
                className={classes.button}
                widthType="content"
                isLoading={isLoading}
                onClick={handleSendTestReminderClick}
              >
                <div className="translate">
                  <FormattedText id="Send a Test Reminder" />
                </div>
              </Button>
              <Button
                type="submit"
                widthType="content"
                isLoading={isLoading}
                onClick={handleSkipAndFinishClick}
                variant="secondary"
              >
                <div className="translate">
                  <FormattedText id="Skip & Finish My Account" />
                </div>
              </Button>
            </Box>
          ) : (
            <Button type="submit" className={classes.button} widthType="content" isLoading={isLoading}>
              <FormattedText id="common.saveAndContinue" />
            </Button>
          )}
        </form>
      </Box>
      <SendTestOrFinishScreen active={showSendTestOrFinish} sendTestReminder={sendTestReminder} />
    </>
  )
}

function buildPhoneNumberDefaults(profile: GetProfileResponseDto, defaultCountryCode: string): PhoneNumber[] {
  const existingPhoneNumbers = (profile.phone_numbers?.map((n) => n?.toString() || '') ?? []).filter(Boolean)
  if (existingPhoneNumbers.length === 0) {
    existingPhoneNumbers.push('')
  }
  return existingPhoneNumbers.map((phone) => ({
    id: generateRandomId(),
    ...getPhoneAndCountryCode(phone, defaultCountryCode),
  }))
}

function collectValidPhoneNumbers(phones: PhoneNumber[]): { numbers: string[]; invalidIndex: number | null } {
  const numbers = new Set<string>()
  for (const [index, phone] of phones.entries()) {
    const phoneNumber = `+${(phone.phone ?? '').replace(/\D/g, '')}`
    const parsed = parsePhoneNumberFromString(phoneNumber)
    if (!parsed?.isValid()) {
      return { numbers: [], invalidIndex: index }
    }
    numbers.add(phoneNumber)
  }
  return { numbers: Array.from(numbers), invalidIndex: null }
}
